import type { LinearRGB } from '../../../core/color';
import type { Vec2 } from '../../../geo/types';
import type { Frame, MeshBuilder, VertexStyle } from '../../mesh/meshBuilder';
import { SURFACE } from '../../mesh/attributes';
import {
  beamSolid,
  boxSolid,
  frustumSolid,
  planPoint,
  prismSolid,
  ringPoints,
  orientedFace,
} from '../solids';
import type { MarketColors, MarketStall } from './marketStalls';

const CLOSED = { top: true, bottom: true };
const TABLE = 1.0;

function tray(b: MeshBuilder, wood: VertexStyle, frame: Frame, u: number, v: number): void {
  boxSolid(
    b,
    wood,
    frame,
    [u - 0.62, TABLE + 0.01, -v - 0.42],
    [u + 0.62, TABLE + 0.05, -v + 0.42],
    CLOSED
  );
  for (const side of [-1, 1]) {
    boxSolid(
      b,
      wood,
      frame,
      [u - 0.64, TABLE + 0.03, -v + side * 0.42 - 0.025],
      [u + 0.64, TABLE + 0.18, -v + side * 0.42 + 0.025],
      CLOSED
    );
    boxSolid(
      b,
      wood,
      frame,
      [u + side * 0.64 - 0.025, TABLE + 0.03, -v - 0.42],
      [u + side * 0.64 + 0.025, TABLE + 0.18, -v + 0.42],
      CLOSED
    );
  }
}

/** Low fish laid on the boards: a tapered body, forked tail, gill and two small dark eyes. */
function fish(
  b: MeshBuilder,
  styles: { silver: VertexStyle; dark: VertexStyle },
  frame: Frame,
  u: number,
  v: number,
  size: number
): void {
  const outline: Vec2[] = [
    [-0.22, 0],
    [-0.12, -0.06],
    [0.09, -0.065],
    [0.22, -0.02],
    [0.25, 0],
    [0.22, 0.02],
    [0.09, 0.065],
    [-0.12, 0.06],
  ];
  prismSolid(
    b,
    styles.silver,
    frame,
    outline.map(([x, y]) => [u + x * size, v + y * size]),
    TABLE + 0.055,
    TABLE + 0.105,
    CLOSED
  );
  const tail: Vec2[] = [
    [u - 0.18 * size, v],
    [u - 0.31 * size, v - 0.08 * size],
    [u - 0.27 * size, v],
    [u - 0.31 * size, v + 0.08 * size],
  ];
  prismSolid(b, styles.silver, frame, tail, TABLE + 0.055, TABLE + 0.075, CLOSED);
  for (const side of [-1, 1]) {
    boxSolid(
      b,
      styles.dark,
      frame,
      [u + 0.185 * size, TABLE + 0.106, -v + side * 0.024 * size - 0.009],
      [u + 0.205 * size, TABLE + 0.111, -v + side * 0.024 * size + 0.009],
      CLOSED
    );
  }
  beamSolid(
    b,
    styles.dark,
    frame,
    planPoint(u + 0.11 * size, TABLE + 0.109, v - 0.043 * size),
    planPoint(u + 0.105 * size, TABLE + 0.109, v + 0.043 * size),
    0.005
  );
}

function tub(
  b: MeshBuilder,
  wood: VertexStyle,
  dark: VertexStyle,
  frame: Frame,
  center: Vec2,
  tall: boolean
): void {
  const radius = tall ? 0.3 : 0.36;
  const top = TABLE + (tall ? 0.72 : 0.32);
  const spec = { center, radius: radius * 0.87, segments: 10, phase: 0 };
  frustumSolid(b, wood, frame, spec, TABLE + 0.015, top, radius, CLOSED);
  for (const y of [TABLE + 0.08, top - 0.04]) {
    const outer = ringPoints(center, radius + 0.012, 10, 0);
    const inner = ringPoints(center, radius - 0.012, 10, 0);
    outer.forEach((p, i) => {
      const j = (i + 1) % outer.length,
        q = outer[j]!,
        a = inner[i]!,
        c = inner[j]!;
      const ob = planPoint(p[0], y - 0.012, p[1]),
        on = planPoint(q[0], y - 0.012, q[1]);
      const ot = planPoint(p[0], y + 0.012, p[1]),
        nt = planPoint(q[0], y + 0.012, q[1]);
      const ib = planPoint(a[0], y - 0.012, a[1]),
        bn = planPoint(c[0], y - 0.012, c[1]);
      const it = planPoint(a[0], y + 0.012, a[1]),
        tn = planPoint(c[0], y + 0.012, c[1]);
      const dx = (p[0] + q[0]) / 2 - center[0],
        dz = center[1] - (p[1] + q[1]) / 2;
      orientedFace(b, dark, frame, [ob, on, nt, ot], [dx, 0, dz]);
      orientedFace(b, dark, frame, [ib, bn, tn, it], [-dx, 0, -dz]);
      orientedFace(b, dark, frame, [ot, nt, tn, it], [0, 1, 0]);
      orientedFace(b, dark, frame, [ob, on, bn, ib], [0, -1, 0]);
    });
  }
}

/** Counter stock is illustrative period activity, not a reading of individual fish in the painting.
 * Every item stays within the already collidable bench, leaving both paths and the central aisle open. */
export function buildMarketGoods(
  b: MeshBuilder,
  frame: Frame,
  stalls: readonly MarketStall[],
  colors: MarketColors,
  base: Omit<VertexStyle, 'color' | 'surface'>
): void {
  const wood: VertexStyle = { ...base, color: colors.timber, surface: SURFACE.wood };
  const dark: VertexStyle = {
    ...base,
    color: colors.board.map((c) => c * 0.45) as unknown as LinearRGB,
    surface: SURFACE.linework,
  };
  const silver: VertexStyle = {
    ...base,
    color: colors.ground.map((c, i) => c * [1.08, 1.19, 1.25][i]!) as unknown as LinearRGB,
    surface: SURFACE.cloth,
  };
  stalls.forEach((stall, i) => {
    for (const side of [-1, 1]) {
      const u = stall.u + side * 0.87,
        v = stall.v - side * 0.36;
      tray(b, wood, frame, u, v);
      for (let row = 0; row < 2; row++) {
        for (let col = 0; col < 2; col++)
          fish(
            b,
            { silver, dark },
            frame,
            u - 0.27 + col * 0.54,
            v - 0.14 + row * 0.28,
            0.78 + ((i + row + col) % 3) * 0.08
          );
      }
    }
    tub(b, wood, dark, frame, [stall.u + 0.28, stall.v + (i % 2 ? 1.09 : -1.09)], i % 2 === 0);
    // One scale between each pair of benches, behind the fish trays rather than in the aisle.
    if (i % 2 === 0) {
      const u = stall.u,
        v = stall.v;
      beamSolid(b, wood, frame, planPoint(u, TABLE, v), planPoint(u, 2.0, v), 0.035);
      beamSolid(b, wood, frame, planPoint(u - 0.43, 1.96, v), planPoint(u + 0.43, 1.96, v), 0.025);
      for (const side of [-1, 1]) {
        beamSolid(
          b,
          dark,
          frame,
          planPoint(u + side * 0.36, 1.95, v),
          planPoint(u + side * 0.36, 1.6, v),
          0.006
        );
        boxSolid(
          b,
          wood,
          frame,
          [u + side * 0.36 - 0.13, 1.58, -v - 0.12],
          [u + side * 0.36 + 0.13, 1.61, -v + 0.12],
          CLOSED
        );
      }
    }
  });
}
